import React from 'react';
import { Link, useLocation, useNavigation } from 'react-router-dom';
import { User } from '../../types';
import NotificationsPopover from './NotificationsPopover';
import UserAvatar from '../common/UserAvatar';
import { Logo } from '../common/Logo';
import { Loader2, LogOut } from 'lucide-react';
import { prefetchRoute } from '../../router'; // Import prefetch utility

interface DesktopHeaderProps {
  user: User;
  unreadNewsCount: number;
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  user,
  unreadNewsCount
}) => {
  const location = useLocation();
  const navigation = useNavigation();

  // Detect if we're navigating to a specific path
  const isNavigatingTo = (path: string) => {
    return navigation.state === 'loading' && navigation.location?.pathname === path;
  };

  const getNavLinkClass = (path: string) => {
    const isActive = location.pathname === path;
    const isPending = isNavigatingTo(path);
    return `md:px-3 lg:px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 relative flex items-center gap-2 ${isActive
      ? 'text-white shadow-lg bg-indigo-600/90 ring-2 ring-white/20'
      : isPending
        ? 'text-indigo-600 bg-indigo-100/80'
        : 'text-slate-600 hover:text-indigo-600 hover:bg-white/40'
      }`;
  };

  const LinkWithPrefetch = ({ to, children, className }: { to: string, children: React.ReactNode, className: string }) => (
    <Link
      to={to}
      className={className}
      onMouseEnter={() => prefetchRoute(to)}
      onTouchStart={() => prefetchRoute(to)}
    >
      {children}
    </Link>
  );

  return (
    <header className="hidden md:block sticky top-6 z-50 w-full max-w-7xl mx-auto mt-6 rounded-full bg-white/60 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300">
      <div className="px-6 h-16 flex items-center gap-4 relative">
        {/* Left: Logo */}
        <div className="flex items-center gap-2 flex-1">
          <Logo className="w-8 h-8 text-indigo-600/90 drop-shadow-sm shrink-0" />
          <h1 className="font-bold text-xl hidden lg:block text-indigo-600/90 tracking-tight">TechCoop</h1>
        </div>

        {/* Center: Navigation Links (Desktop) */}
        <nav className="flex items-center gap-1 shrink-0">
          <LinkWithPrefetch to="/" className={getNavLinkClass('/')}>
            {isNavigatingTo('/') && <Loader2 size={14} className="animate-spin" />}
            Cá nhân
          </LinkWithPrefetch>
          <LinkWithPrefetch to="/leaderboard" className={getNavLinkClass('/leaderboard')}>
            {isNavigatingTo('/leaderboard') && <Loader2 size={14} className="animate-spin" />}
            Bảng xếp hạng
          </LinkWithPrefetch>
          <LinkWithPrefetch to="/feed" className={getNavLinkClass('/feed')}>
            {isNavigatingTo('/feed') && <Loader2 size={14} className="animate-spin" />}
            Hoạt động
          </LinkWithPrefetch>
          <LinkWithPrefetch to="/news" className={getNavLinkClass('/news')}>
            {isNavigatingTo('/news') && <Loader2 size={14} className="animate-spin" />}
            Tin tức
            {unreadNewsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full animate-bounce shadow-sm border border-white">
                {unreadNewsCount > 9 ? '9+' : unreadNewsCount}
              </span>
            )}
          </LinkWithPrefetch>
          {user.isAdmin && (
            <LinkWithPrefetch to="/admin" className={getNavLinkClass('/admin')}>
              {isNavigatingTo('/admin') && <Loader2 size={14} className="animate-spin" />}
              Admin
            </LinkWithPrefetch>
          )}
        </nav>

        {/* Right: User Profile & Actions */}
        <div className="flex items-center gap-4 flex-1 justify-end">
          <NotificationsPopover userId={user.id} />
          <LinkWithPrefetch to={`/user/${user.id}`} className="block shrink-0">
            <UserAvatar user={user} className="w-8 h-8 rounded-full border border-slate-200 transition-transform hover:scale-105" alt="Profile" />
          </LinkWithPrefetch>

          <button
            onClick={async () => {
              const { getAuth, signOut } = await import("firebase/auth");
              await signOut(getAuth());
              window.location.href = '/login';
            }}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
            title="Đăng xuất"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
